// Do not remove LottiePlayer
// eslint-disable-next-line no-unused-vars
import * as LottiePlayer from '@dotlottie/player-component'

import { createApp, defineAsyncComponent } from 'vue'
import { createPinia } from 'pinia'
import smoothscroll from 'smoothscroll-polyfill'

import { UikitPlugin, useDefaultValidators } from '@hz/ui-kit'

import { createKeycloak } from '@/keycloak'
import ConfigPLugin from '@/plugins/config'
import i18n from '@/i18n'
import store from '@/store'
import router from '@/router'
import { startMonitoring, reportStartupIssue } from '@/core/logging/sentry'
import { config } from '@/core/config'
import { GtmPlugin } from '@/plugins'
import formatDateFromNow from '@/core/format-date-from-now'
import { extendDefaultValidators } from '@/helpers/validation'
import { initDirectives } from '@/directives'
import { usePermissionsStore } from '@/stores/permissions'

import HorizIoLoader from '@/components/HorizIoLoader.vue'

// Add kit styles
import '@hz/ui-kit/css'
import { useHzTolgee } from '@/tolgee'
import { startAppErrorsHandler } from '@/errors'
import { useLoaderStore } from '@/stores/loader'
import { useAuthStore } from '@/stores/auth'
import { useUserActionHistoryStore } from '@/stores/userActionHistory'
import { useUserProfileStore } from '@/stores/userProfile'
import { useAppNotificationsStore } from '@/stores/appNotifications'

// Add polyfills
smoothscroll.polyfill()

let loaderApp = null
let loaderMountedApp = null

export function displayAppLoading() {
  loaderApp = createApp(HorizIoLoader)
  loaderMountedApp = loaderApp.mount('#horiz-io-loader')
}

export function unmountAppLoading() {
  setTimeout(() => {
    loaderMountedApp.hide()
  }, 700)
  setTimeout(() => {
    loaderApp.unmount()
  }, 1400)
}

export function displayAppNotCreated() {
  const app = createApp(defineAsyncComponent(() => import('@/views/AppNotCreated.vue')))

  app.use(ConfigPLugin)

  app.use(i18n)
  app.use(store)

  app.mount('#app')
}

// Plusieurs chemins de keycloak-js (iframe de statut de session, sonde silencieuse) et l'appel
// UMA de `identity.ready` n'ont AUCUN delai maximum : ils ne rejettent jamais. Dans ce cas le
// chargeur tourne indefiniment, le bloc catch n'est jamais atteint, et la panne est totalement
// invisible. Ce chien de garde est la seule facon de la voir.
const DELAI_DEMARRAGE_MS = 20000
let applicationMontee = false

function signalerDemarrageMonte() {
  applicationMontee = true
}

function armerChienDeGardeDemarrage() {
  setTimeout(() => {
    if (!applicationMontee) {
      reportStartupIssue(
        new Error(`L'application n'a pas demarre au bout de ${DELAI_DEMARRAGE_MS} ms`),
        'startup-watchdog',
      )
    }
  }, DELAI_DEMARRAGE_MS)
}

async function start() {
  try {
    const pinia = createPinia()

    // special App.vue for the PDF of the simulation report
    if (window.location.pathname.startsWith('/pdf-report')) {
      const app = createApp(defineAsyncComponent(() => import('@/AppPDF.vue')))
      app.use(i18n)
      await useHzTolgee(app)

      app.use(pinia)
      await createKeycloak()

      app.use(store)
      app.use(router)

      app.mount('#app')
      signalerDemarrageMonte()
    } else {
      displayAppLoading()
      const app = createApp(defineAsyncComponent(() => import('@/App.vue')))
      startAppErrorsHandler(app)
      startMonitoring(app, router)
      app.use(pinia)

      await createKeycloak()

      try {
        await usePermissionsStore().refreshQuotasAndPermissions()
      } catch (e) {
        // Second point de rupture du demarrage, mis en evidence sur la preproduction : cet
        // appel reseau precede l'installation du routeur, et son echec faisait tomber toute
        // l'application.
        reportStartupIssue(e, 'permissions')

        // Pour un client CONNECTE on laisse l'erreur remonter : mieux vaut un message clair
        // qu'une application silencieusement amputee de ses fonctions.
        // Pour un visiteur ANONYME on continue : les permissions par defaut sont les plus
        // restrictives, et il doit pouvoir atteindre l'inscription et le paiement.
        if (useAuthStore().isLoggedIn) {
          throw e
        }
      }

      if (useAuthStore().isLoggedIn) {
        await Promise.all([
          useUserActionHistoryStore().fetch(),
          useUserProfileStore().fetchUserProfile(),
          useAppNotificationsStore().startFetchUnreadCount(),
        ])
      }

      app.use(ConfigPLugin)

      const trackingIsEnable = config.features.tracking

      app.use(GtmPlugin, {
        url: config.google.gtm.url,
        containerId: config.google.gtm.containerId,
        isEnabled: trackingIsEnable,
        router,
      })

      app.use(i18n)
      await useHzTolgee(app)
      app.use(store)
      app.use(router)

      app.use(UikitPlugin, {
        i18n,
        helpers: {
          formatDateFromNow,
        },
      })

      useDefaultValidators(i18n)
      extendDefaultValidators()

      initDirectives(app)

      app.mount('#app')
      signalerDemarrageMonte()
      app.config.globalProperties.$app = app
      useLoaderStore().start(loaderMountedApp)
    }
  } catch (e) {
    // Cette panne empeche l'application entiere de se lancer. Elle n'etait ecrite que dans la
    // console du navigateur : invisible cote support, donc jamais detectee.
    reportStartupIssue(e, 'app-start')
    displayAppNotCreated()
    unmountAppLoading()
  }
}

armerChienDeGardeDemarrage()
start()
